import React, { useState, useEffect } from "react";
import Header from "../../components/header";
import Footer from "../../components/footer";
import AppTextField from "../../components/text-field/component";
import { useNavigate, useParams } from "react-router-dom";
import PathHelper from "../../utils/path-helper";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Loader from "../../components/loader";
import moment from "moment";
import { readCookie } from "../../utils/helpers";

const CANCEL_REASON_TYPES = {
  DEFAULT: "Default",
  OTHER: "Other",
};

const CANCEL_REASONS = [
  "price_expensive",
  "no_features",
  "product_not_good",
  "unrecognized_charge",
  "does_not_meet_needs",
  "not_free",
  "limited_content",
  "temporary_need",
  "competitor_cheaper",
  "use_less_often",
  "poor_customer_experience",
  "other",
];

function CancelPlanFeedBack({
  funnelStep,
  submitCancellationFeedbackConnect,
  accountCancellationConnect,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const settingsPath = PathHelper(APP_NAVIGATION.SETTINGS, currentLocale);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [accessUntilDate, setAccessUntilDate] = useState("");

  const { userGUID, email } = funnelStep || {};

  const isOtherSelected = selectedReason === CANCEL_REASON_TYPES.OTHER;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const nextBillingDate = readCookie("nextBillingDate");
    if (nextBillingDate) {
      setAccessUntilDate(moment(nextBillingDate).format("MMMM DD, YYYY"));
    }
  }, []);

  const handleAccountCancellation = () => {
    const data = {
      isEndOfTerm: true,
      comment: "USER:CancelAccount",
      userGuid: userGUID,
      locale: currentLocale,
    };
    setIsLoading(true);

    accountCancellationConnect(data)
      .then((res) => {
        if (res.data) {
          toast.success(t("settings.account_cancle_success_msg"));
          // after successful submission navigate to dashboard
          navigate(`${settingsPath.next}?steps=billing`);
        }
      })
      .catch((err) => {
        console.log({ err });
        setIsLoading(false);
        toast.error("settings.account_cancle_fail_msg");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleReasonChange = (reason) => {
    setSelectedReason(reason);

    if (reason !== CANCEL_REASON_TYPES.OTHER) {
      setOtherReason("");
    }
  };

  const handleSubmit = async () => {
    const payload = {
      UserId: userGUID || null,
      Reason: isOtherSelected ? otherReason.trim() : selectedReason,
      ReasonType: isOtherSelected
        ? CANCEL_REASON_TYPES.OTHER
        : CANCEL_REASON_TYPES.DEFAULT,
    };

    try {
      setLoading(true);
      await submitCancellationFeedbackConnect(payload);
      handleAccountCancellation();
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  return (
    <>
      <Header />

      <div className="min-h-screen bg-gray-50 px-6 py-10 sm:px-10 lg:px-16">
        <div className="max-w-3xl">
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t("cancelplanfeedback.cancele_membership")}
          </h1>

          <p className="mt-4 text-gray-500">
            {t("cancelplanfeedback.email_confirmation")}{" "}
            <span className="font-medium">{email}</span>.{" "}
            {t("cancelplanfeedback.until")}{" "}
            <span className="font-semibold text-gray-700">
              {accessUntilDate}
            </span>
            .
          </p>

          <p className="mt-10 text-lg font-semibold text-gray-800">
            {t("cancelplanfeedback.improving")}
          </p>

          <p className="mt-6 font-semibold text-gray-800">
            {t("cancelplanfeedback.reason_cancel")}
          </p>

          <div className="mt-6 space-y-4">
            {CANCEL_REASONS.map((reasonKey) => (
              <div key={reasonKey}>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reasonKey}
                    checked={selectedReason === reasonKey}
                    onChange={() => handleReasonChange(reasonKey)}
                    className="h-5 w-5 border-gray-300 text-orange-500 focus:ring-orange-400"
                  />

                  <span className="text-gray-700">
                    {t(`cancelplanfeedback.cancel_reasons.${reasonKey}`)}
                  </span>
                </label>

                {reasonKey === "other" && isOtherSelected && (
                  <div className="ml-8 mt-3">
                    <AppTextField
                      placeholder={t(
                        "cancelplanfeedback.other_reason_placeholder",
                      )}
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      inputClassName="w-full"
                      maxLength={250}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              loading ||
              !selectedReason ||
              (isOtherSelected && !otherReason.trim())
            }
            className="mt-8 rounded-full bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {loading
              ? t("cancelplanfeedback.submitting")
              : t("cancelplanfeedback.submit")}
          </button>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default CancelPlanFeedBack;
